// src/components/admin/AnalyticsTestPanel.js
import { useState } from 'react';
import { 
  collection, 
  doc, 
  writeBatch, 
  getDoc, 
  getDocs, 
  deleteDoc,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../../firebase';

const AnalyticsTestPanel = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isCreatingPodcasts, setIsCreatingPodcasts] = useState(false);
  const [testResults, setTestResults] = useState('');
  const [testStatus, setTestStatus] = useState(''); // 'success', 'error', 'warning', ''

  // Test user profiles with realistic listening patterns
  const testUsers = [
    { id: 'testuser_heavy', type: 'Heavy Listener' },
    { id: 'testuser_casual', type: 'Casual Listener' },
    { id: 'testuser_binge', type: 'Binge Listener' }
  ];

  // Test podcasts - will be created in Firebase
  const testPodcastsData = [
    {
      id: 'test_tech_podcast',
      title: 'Test Tech Podcast',
      description: 'A test podcast about technology, programming, and digital innovation. Perfect for testing analytics and revenue calculations.',
      category: 'Technology',
      imageURL: 'https://via.placeholder.com/400x400/667eea/ffffff?text=Tech'
    },
    {
      id: 'test_history_show',
      title: 'Test History Show', 
      description: 'Exploring historical events and fascinating stories from the past. Great for testing diverse audience engagement.',
      category: 'History',
      imageURL: 'https://via.placeholder.com/400x400/e74c3c/ffffff?text=History'
    },
    {
      id: 'test_comedy_cast',
      title: 'Test Comedy Cast',
      description: 'Humor, jokes, and funny conversations to brighten your day. Testing comedy content analytics.',
      category: 'Comedy',
      imageURL: 'https://via.placeholder.com/400x400/f39c12/ffffff?text=Comedy'
    },
    {
      id: 'test_business_talk',
      title: 'Test Business Talk',
      description: 'Entrepreneurship, business strategies, and startup stories. Testing business content performance.',
      category: 'Business',
      imageURL: 'https://via.placeholder.com/400x400/27ae60/ffffff?text=Business'
    }
  ];

  const createTestPodcasts = async () => {
    setIsCreatingPodcasts(true);
    setTestResults('Creating test podcasts with episodes...');
    setTestStatus('warning');

    try {
      const batch = writeBatch(db);
      const currentUser = 'z9LWkEPkgHXbXbrs1bejwEZrZRz2'; // Your actual user ID
      let totalPodcasts = 0;
      let totalEpisodes = 0;

      for (const podcastData of testPodcastsData) {
        // Create episodes for this podcast
        const episodes = {};
        for (let i = 1; i <= 4; i++) {
          const episodeId = `-${Math.floor(Math.random() * 9000000000000000000)}`;
          episodes[episodeId] = {
            id: episodeId,
            title: `${podcastData.title} - Episode ${i}`,
            description: `This is episode ${i} of ${podcastData.title}. Testing episode analytics and individual tracking capabilities.`,
            audioURL: `https://example.com/audio/${podcastData.id}_episode_${i}.mp3`,
            duration: Math.floor(Math.random() * 3600) + 1800, // 30min - 90min
            publishedAt: Timestamp.fromDate(new Date(Date.now() - (i * 7 * 24 * 60 * 60 * 1000))), // Weekly episodes
            likes: 0,
            dislikes: 0,
            likedBy: [],
            dislikedBy: [],
            listens: 0
          };
          totalEpisodes++;
        }

        // Create podcast document
        const podcastRef = doc(db, 'podcasts', podcastData.id);
        const podcastDoc = {
          category: podcastData.category,
          createdAt: Timestamp.now(),
          creator: 'martinslorenzen',
          creatorId: currentUser,
          description: podcastData.description,
          earnings: 0,
          episodes: episodes,
          imageURL: podcastData.imageURL,
          title: podcastData.title,
          // Import status fields
          importStatus: 'success', // success, partial, failed
          importDetails: {
            rssUrl: `https://feeds.example.com/${podcastData.id}`,
            lastImport: Timestamp.now(),
            episodesImported: 4,
            errors: []
          }
        };

        batch.set(podcastRef, podcastDoc);
        totalPodcasts++;
      }

      await batch.commit();

      setTestResults(`✅ Test podcasts created successfully! Created ${totalPodcasts} podcasts with ${totalEpisodes} episodes total.`);
      setTestStatus('success');

    } catch (error) {
      console.error('Error creating test podcasts:', error);
      setTestResults(`❌ Error creating test podcasts: ${error.message}`);
      setTestStatus('error');
    } finally {
      setIsCreatingPodcasts(false);
    }
  };

  const generateTestData = async () => {
    setIsGenerating(true);
    setTestResults('Generating 30 days of realistic listening data...');
    setTestStatus('warning');

    try {
      const batch = writeBatch(db);
      const today = new Date();
      let totalSessions = 0;

      // Use test podcast IDs that we created
      const testPodcasts = testPodcastsData.map(p => ({ id: p.id, title: p.title }));

      // Generate 30 days of data for each test user
      for (const user of testUsers) {
        for (let dayOffset = 0; dayOffset < 30; dayOffset++) {
          const date = new Date(today);
          date.setDate(date.getDate() - dayOffset);

          const sessionsForDay = getSessionsForUserType(user.type, date);

          for (const sessionInfo of sessionsForDay) {
            const podcast = testPodcasts[Math.floor(Math.random() * testPodcasts.length)];
            const episodeId = `ep_${Math.floor(Math.random() * 4) + 1}`;

            const startTime = new Date(date);
            startTime.setHours(sessionInfo.hour, 0, 0, 0);

            const endTime = new Date(startTime);
            endTime.setSeconds(endTime.getSeconds() + sessionInfo.duration);

            const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

            // Save to users/{userId}/listening_history/{sessionId}
            const sessionRef = doc(db, 'users', user.id, 'listening_history', sessionId);
            const sessionData = {
              userId: user.id,
              podcastId: podcast.id,
              episodeId: episodeId,
              startTime: Timestamp.fromDate(startTime),
              endTime: Timestamp.fromDate(endTime),
              duration: sessionInfo.duration,
              completionPercentage: sessionInfo.completion,
              displayTitle: `${podcast.title} - Episode ${Math.floor(Math.random() * 4) + 1}`,
              displayPodcastTitle: podcast.title,
              validatedDuration: sessionInfo.duration >= 30 ? sessionInfo.duration : 0,
              month: formatMonth(startTime),
              year: startTime.getFullYear()
            };

            batch.set(sessionRef, sessionData);
            totalSessions++;
          }
        }

        // Create monthly summaries for each user
        await createMonthlySummaries(batch, user.id);
      }

      await batch.commit();

      setTestResults(`✅ Test data generated successfully! Created ${totalSessions} listening sessions for 3 test users over 30 days.`);
      setTestStatus('success');
      
      // Validate after a short delay
      setTimeout(() => validateTestData(), 2000);

    } catch (error) {
      console.error('Error generating test data:', error);
      setTestResults(`❌ Error generating test data: ${error.message}`);
      setTestStatus('error');
    } finally {
      setIsGenerating(false);
    }
  };

  const createMonthlySummaries = async (batch, userId) => {
    // Get current month for summary
    const currentMonth = formatMonth(new Date());
    const summaryRef = doc(db, 'users', userId, 'monthly_summaries', currentMonth);

    // Create realistic summary with test podcast data
    const podcastTimes = {};
    testPodcastsData.forEach(podcast => {
      podcastTimes[podcast.id] = Math.random() * 7200 + 1800; // 30min - 2hours per podcast
    });

    const summaryData = {
      userId: userId,
      month: currentMonth,
      totalValidatedTime: Object.values(podcastTimes).reduce((sum, time) => sum + time, 0),
      podcastTimes: podcastTimes,
      episodeCount: Math.floor(Math.random() * 20) + 10,
      lastUpdated: Timestamp.now()
    };

    batch.set(summaryRef, summaryData, { merge: true });
  };

  const testGDPRDeletion = async () => {
    setIsTesting(true);
    setTestResults('Testing GDPR deletion (complete user removal)...');
    setTestStatus('warning');

    try {
      const testUserId = 'testuser_heavy';

      // First verify user exists
      const userDoc = await getDoc(doc(db, 'users', testUserId));
      if (!userDoc.exists()) {
        setTestResults('❌ No test user found to delete. Generate test data first.');
        setTestStatus('error');
        setIsTesting(false);
        return;
      }

      // Delete all user data
      const success = await deleteAllUserData(testUserId);
      
      if (success) {
        // Verify deletion
        const verificationResult = await verifyUserDeleted(testUserId);
        
        if (verificationResult.success) {
          setTestResults(`✅ GDPR deletion test PASSED! User completely removed from all collections.`);
          setTestStatus('success');
        } else {
          setTestResults(`❌ GDPR deletion test FAILED! ${verificationResult.details}`);
          setTestStatus('error');
        }
      } else {
        setTestResults('❌ GDPR deletion failed during execution.');
        setTestStatus('error');
      }

    } catch (error) {
      console.error('Error testing GDPR deletion:', error);
      setTestResults(`❌ GDPR test error: ${error.message}`);
      setTestStatus('error');
    } finally {
      setIsTesting(false);
    }
  };

  const deleteAllUserData = async (userId) => {
    try {
      const batch = writeBatch(db);

      // Delete listening history
      const listeningHistoryRef = collection(db, 'users', userId, 'listening_history');
      const listeningDocs = await getDocs(listeningHistoryRef);
      listeningDocs.forEach(doc => batch.delete(doc.ref));

      // Delete monthly summaries
      const monthlySummariesRef = collection(db, 'users', userId, 'monthly_summaries');
      const summaryDocs = await getDocs(monthlySummariesRef);
      summaryDocs.forEach(doc => batch.delete(doc.ref));

      // Delete user document
      batch.delete(doc(db, 'users', userId));

      await batch.commit();
      return true;
    } catch (error) {
      console.error('Error deleting user data:', error);
      return false;
    }
  };

  const verifyUserDeleted = async (userId) => {
    try {
      // Check user document
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        return { success: false, details: 'User document still exists' };
      }

      // Check listening history
      const listeningDocs = await getDocs(collection(db, 'users', userId, 'listening_history'));
      if (!listeningDocs.empty) {
        return { success: false, details: `${listeningDocs.size} listening sessions still exist` };
      }

      // Check monthly summaries
      const summaryDocs = await getDocs(collection(db, 'users', userId, 'monthly_summaries'));
      if (!summaryDocs.empty) {
        return { success: false, details: `${summaryDocs.size} monthly summaries still exist` };
      }

      return { success: true, details: 'User completely removed' };
    } catch (error) {
      return { success: false, details: `Verification error: ${error.message}` };
    }
  };

  const removeAllTestData = async () => {
    setIsRemoving(true);
    setTestResults('Removing all test data (users and podcasts)...');
    setTestStatus('warning');

    try {
      let deletedUsers = 0;
      let deletedPodcasts = 0;
      
      // Delete test users
      for (const user of testUsers) {
        const success = await deleteAllUserData(user.id);
        if (success) deletedUsers++;
      }

      // Delete test podcasts
      const batch = writeBatch(db);
      for (const podcastData of testPodcastsData) {
        batch.delete(doc(db, 'podcasts', podcastData.id));
        deletedPodcasts++;
      }
      await batch.commit();

      setTestResults(`✅ All test data removed! ${deletedUsers} test users and ${deletedPodcasts} test podcasts deleted.`);
      setTestStatus('success');

    } catch (error) {
      console.error('Error removing test data:', error);
      setTestResults(`❌ Error removing test data: ${error.message}`);
      setTestStatus('error');
    } finally {
      setIsRemoving(false);
    }
  };

  const validateTestData = async () => {
    try {
      let validationResults = [];
      
      for (const user of testUsers) {
        const listeningDocs = await getDocs(collection(db, 'users', user.id, 'listening_history'));
        const summaryDocs = await getDocs(collection(db, 'users', user.id, 'monthly_summaries'));
        
        validationResults.push(`${user.type}: ${listeningDocs.size} sessions, ${summaryDocs.size} summaries`);
      }

      const validationText = validationResults.join(' | ');
      setTestResults(prev => `${prev}\n\n📊 Validation: ${validationText}`);
    } catch (error) {
      console.error('Error validating test data:', error);
    }
  };

  // Helper functions
  const getSessionsForUserType = (userType, date) => {
    switch (userType) {
      case 'Heavy Listener':
        return [
          { hour: 8, duration: Math.random() * 1800 + 1800, completion: Math.random() * 0.3 + 0.7 },
          { hour: 12, duration: Math.random() * 900 + 900, completion: Math.random() * 0.3 + 0.6 },
          { hour: 17, duration: Math.random() * 1800 + 1800, completion: Math.random() * 0.3 + 0.7 },
          { hour: 21, duration: Math.random() * 1200 + 1200, completion: Math.random() * 0.2 + 0.8 }
        ];
      
      case 'Casual Listener':
        const sessions = Math.random() > 0.5 ? 2 : 1;
        if (sessions === 1) {
          return [{ hour: 8, duration: Math.random() * 1200 + 1200, completion: Math.random() * 0.3 + 0.5 }];
        } else {
          return [
            { hour: 8, duration: Math.random() * 900 + 900, completion: Math.random() * 0.3 + 0.5 },
            { hour: 19, duration: Math.random() * 900 + 900, completion: Math.random() * 0.3 + 0.6 }
          ];
        }
      
      case 'Binge Listener':
        const intensity = Math.random() * 10;
        if (intensity < 3) {
          return []; // No listening some days
        } else if (intensity > 7) {
          return [
            { hour: 10, duration: Math.random() * 3600 + 3600, completion: Math.random() * 0.2 + 0.8 },
            { hour: 15, duration: Math.random() * 3000 + 2400, completion: Math.random() * 0.25 + 0.7 },
            { hour: 20, duration: Math.random() * 1800 + 1800, completion: Math.random() * 0.3 + 0.6 }
          ];
        } else {
          return [{ hour: Math.floor(Math.random() * 12) + 9, duration: Math.random() * 1800 + 1800, completion: Math.random() * 0.3 + 0.6 }];
        }
      
      default:
        return [];
    }
  };

  const formatMonth = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  const getStatusIcon = () => {
    switch (testStatus) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'warning': return '⚠️';
      default: return null;
    }
  };

  const getStatusColor = () => {
    switch (testStatus) {
      case 'success': return 'border-green-400 bg-green-600/10';
      case 'error': return 'border-red-400 bg-red-600/10';
      case 'warning': return 'border-yellow-400 bg-yellow-600/10';
      default: return 'border-slate-600 bg-slate-700/50';
    }
  };

  if (process.env.NODE_ENV === 'production') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-slate-400 text-lg font-medium">Ikke tilgængelig i produktion</p>
          <p className="text-slate-500 text-sm mt-2">Dette panel er kun tilgængeligt i udviklingsmiljøet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Analytics & GDPR Testing</h1>
        <p className="text-slate-400 mt-1">Test your analytics system and GDPR compliance</p>
      </div>

      {/* Test Controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button
          onClick={createTestPodcasts}
          disabled={isCreatingPodcasts}
          className="bg-purple-600 hover:bg-purple-700 disabled:bg-purple-800 disabled:cursor-not-allowed text-white p-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
        >
          <span className="text-xl">🎙️</span>
          <span>{isCreatingPodcasts ? 'Creating...' : 'Create Test Podcasts'}</span>
        </button>

        <button
          onClick={generateTestData}
          disabled={isGenerating}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 disabled:cursor-not-allowed text-white p-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
        >
          <span className="text-xl">🧪</span>
          <span>{isGenerating ? 'Generating...' : 'Generate Test Data'}</span>
        </button>

        <button
          onClick={testGDPRDeletion}
          disabled={isTesting}
          className="bg-orange-600 hover:bg-orange-700 disabled:bg-orange-800 disabled:cursor-not-allowed text-white p-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
        >
          <span className="text-xl">🛡️</span>
          <span>{isTesting ? 'Testing...' : 'Test GDPR Deletion'}</span>
        </button>

        <button
          onClick={removeAllTestData}
          disabled={isRemoving}
          className="bg-red-600 hover:bg-red-700 disabled:bg-red-800 disabled:cursor-not-allowed text-white p-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
        >
          <span className="text-xl">🗑️</span>
          <span>{isRemoving ? 'Removing...' : 'Remove All Test Data'}</span>
        </button>
      </div>

      {/* Test Results */}
      {testResults && (
        <div className={`p-4 rounded-lg border ${getStatusColor()}`}>
          <div className="flex items-start space-x-3">
            <span className="text-xl">{getStatusIcon()}</span>
            <div className="flex-1">
              <pre className="text-sm text-white whitespace-pre-wrap font-mono">{testResults}</pre>
            </div>
          </div>
        </div>
      )}

      {/* Test User Info */}
      <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
        <h3 className="text-lg font-semibold text-white mb-4">Test User Profiles</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {testUsers.map((user) => (
            <div key={user.id} className="bg-slate-700 p-4 rounded-lg">
              <h4 className="font-medium text-white">{user.type}</h4>
              <p className="text-sm text-slate-400 mt-1">ID: {user.id}</p>
              <p className="text-xs text-slate-500 mt-2">
                {user.type === 'Heavy Listener' && '3-4 hours/day, diverse tastes'}
                {user.type === 'Casual Listener' && '30min/day, focused listening'}
                {user.type === 'Binge Listener' && 'Variable: 6+ hours some days, zero others'}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Test Podcasts Info */}
      <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
        <h3 className="text-lg font-semibold text-white mb-4">Test Podcasts</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {testPodcastsData.map((podcast) => (
            <div key={podcast.id} className="bg-slate-700 p-4 rounded-lg">
              <h4 className="font-medium text-white">{podcast.title}</h4>
              <p className="text-sm text-slate-400 mt-1">Genre: {podcast.category}</p>
              <p className="text-xs text-slate-500 mt-2">4 episodes each, realistic analytics data</p>
            </div>
          ))}
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
        <h3 className="text-lg font-semibold text-white mb-4">How to Use</h3>
        <ol className="text-slate-300 space-y-2 text-sm">
          <li>1. <strong className="text-white">Create Test Podcasts</strong> - Creates 4 realistic podcasts with episodes owned by your user ID</li>
          <li>2. <strong className="text-white">Generate Test Data</strong> - Creates 3 users with 30 days of listening history for your test podcasts</li>
          <li>3. <strong className="text-white">Check your Creator Dashboard</strong> - Should now show real analytics instead of zeros</li>
          <li>4. <strong className="text-white">Test GDPR Deletion</strong> - Completely removes one user and verifies they're gone</li>
          <li>5. <strong className="text-white">Remove All Test Data</strong> - Clean up users and podcasts when done testing</li>
        </ol>
      </div>
    </div>
  );
};

export default AnalyticsTestPanel;